// ============================================================================
// Edit one logistics booking's operationally-entered fields. Systemic fields
// (state, provider, derived status) are read-only — those move through the
// pipeline, not by hand. provider_ref is editable only for MANUAL providers
// (own_tms uses it as the carrier's key; the server also guards this).
// ============================================================================
import { DatePicker, Form, Input, Modal, Select } from "antd";
import dayjs from "dayjs";
import React, { useEffect } from "react";
import ModalBody, { FLUSH_MODAL, MODAL_BODY_STYLE } from "../../components/modal_body";
import { TeamMember, updateLogisticsOrder } from "../logistics.api";
import { trackingUrlRules } from "../porter_link";
import { LogisticsOrderEdit, LogisticsOrderRow } from "../../types";

interface Props {
  open: boolean;
  order: LogisticsOrderRow | null;
  teamMembers: TeamMember[]; // the ops team, for the Owner (assigned-to) dropdown
  onClose: () => void;
  onSaved: (updated: LogisticsOrderRow) => void;
}

const EditLogisticsModal: React.FC<Props> = ({ open, order, teamMembers, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = React.useState(false);
  const isOwnTms = order?.provider_type === "own_tms";
  // A Porter link is never required, but one that IS entered gets a warning if it carries no
  // tracking details — otherwise the automatic delivered-marking skips this booking with nobody
  // any the wiser.
  const isPorter = order?.provider_type === "porter";

  useEffect(() => {
    if (order) {
      form.setFieldsValue({
        vehicle_number: order.vehicle_number || "",
        driver_name: order.driver_name || "",
        driver_number: order.driver_number || "",
        awb_no: order.awb_no || "",
        tracking_url: order.tracking_url || "",
        provider_ref: order.provider_ref || "",
        eta: order.eta ? dayjs(order.eta) : null,
        assigned_to: order.assigned_to || undefined, // the owner's user id (undefined = unassigned)
      });
    }
  }, [order, form]);

  const handleSave = async () => {
    if (!order) return;
    const values = await form.validateFields();
    const body: LogisticsOrderEdit = {
      vehicle_number: values.vehicle_number ?? "",
      driver_name: values.driver_name ?? "",
      driver_number: values.driver_number ?? "",
      awb_no: values.awb_no ?? "",
      tracking_url: values.tracking_url ?? "",
      eta: values.eta ? dayjs(values.eta).toISOString() : null,
      assigned_to: values.assigned_to ?? null, // a user id, or null to unassign
    };
    // Only send provider_ref for manual providers — own_tms is server-guarded and would 400.
    if (!isOwnTms) body.provider_ref = values.provider_ref ?? "";

    setSaving(true);
    const updated = await updateLogisticsOrder(order.id, body);
    setSaving(false);
    if (updated) {
      onSaved(updated);
      onClose();
    }
  };

  return (
    <Modal
      title={`Edit booking #${order?.id ?? ""}`}
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText="Save"
      confirmLoading={saving}
      destroyOnClose
      // Antd anchors a modal 100px from the top by default; this form is taller than that leaves,
      // so Save fell off the bottom of the screen. Centre it and scroll the body instead.
      centered
      className={FLUSH_MODAL}
      bodyStyle={MODAL_BODY_STYLE}
    >
      <ModalBody>
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item label="Vehicle number" name="vehicle_number">
            <Input placeholder="e.g. KA01AB1234" maxLength={32} allowClear />
          </Form.Item>
          <Form.Item label="Driver name" name="driver_name">
            <Input maxLength={128} allowClear />
          </Form.Item>
          <Form.Item label="Driver number" name="driver_number">
            <Input maxLength={20} allowClear />
          </Form.Item>
          <Form.Item label="AWB / LR number" name="awb_no">
            <Input maxLength={64} allowClear />
          </Form.Item>
          <Form.Item
            label={isPorter ? "Porter live-tracking link (optional)" : "Tracking URL"}
            name="tracking_url"
            rules={trackingUrlRules(isPorter)}
            extra={
              isPorter
                ? "The share link from the Porter app — paste it and we'll mark this delivered automatically when Porter completes the trip."
                : undefined
            }
          >
            <Input placeholder="https://…" allowClear />
          </Form.Item>
          <Form.Item
            label="Carrier's booking number"
            name="provider_ref"
            extra={
              isOwnTms
                ? "Set automatically for our own fleet — cannot be changed here."
                : "The number the partner gave you (Porter / intercity LR)."
            }
          >
            <Input maxLength={255} allowClear disabled={isOwnTms} />
          </Form.Item>
          <Form.Item label="Expected arrival (ETA)" name="eta">
            <DatePicker showTime format="DD MMM YYYY HH:mm" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Assigned to"
            name="assigned_to"
            extra="The ops person who owns this booking on the board."
          >
            <Select
              placeholder="Unassigned"
              allowClear
              showSearch
              options={teamMembers.map((m) => ({ value: m.value, label: m.label }))}
              filterOption={(input, opt) =>
                String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default EditLogisticsModal;
